import polly from 'polly-js';

import { appConfig } from '../config/config.app';
import { actually } from '../utils/logging';

import { KNOWN_ERROR_CODES } from './constants';

export type HttPostArgs = {
  headers?: Headers;
  body: string;
};

const call = async (
  url: string,
  body?: string,
  headers?: Headers,
  requestOverrides?: RequestInit,
  sendCorrelationId = true,
  sendCredentials = true,
  externalEndpoint = false,
): Promise<Response> => {
  const reqHeaders = new Headers({ 'Content-Type': 'application/json' });

  headers?.forEach((value, key) => {
    reqHeaders.set(key, value);
  });

  if (!externalEndpoint) {
    if (sendCorrelationId && window.mgm?.logger) {
      reqHeaders.append(window.mgm.logger.JOURNEY_ID_KEY, window.mgm.logger.getJourneyId());
      reqHeaders.append(window.mgm.logger.CORRELATION_ID_KEY, window.mgm.logger.getCorrelationId());
    }

    const token = await window.mgm?.authClient?.getAccessToken();
    if (token) {
      reqHeaders.append('Authorization', `Bearer ${token.accessToken}`);
    }
  }

  const requestOptions: RequestInit = {
    headers: reqHeaders,
    body: body,
    credentials: sendCredentials ? 'include' : undefined,
    ...requestOverrides,
  };

  let result: Response = {} as Response;

  const callEndpoint = async () => {
    result = await polly()
      .handle(error => {
        window.mgm?.logger?.debug('Polly Failure', {
          url,
          error,
        });
        return error instanceof TypeError;
      })
      .waitAndRetry([100, 200])
      .executeForPromise(() => fetch(url, requestOptions));
  };

  if (window.mgm?.logger) {
    await window.mgm.logger.captureTiming(callEndpoint, 'Request Timing', {
      url,
      journeyId: String(reqHeaders.get(window.mgm.logger.JOURNEY_ID_KEY)),
      correlationId: String(reqHeaders.get(window.mgm.logger.CORRELATION_ID_KEY)),
    });
  } else {
    await callEndpoint();
  }

  return result;
};

export const post = async (
  path: string,
  params: HttPostArgs,
  apiUrl = appConfig.apiUrl,
  sendCorrelationId = true,
  sendCredentials = true,
  externalEndpoint = false,
) => {
  try {
    const url = externalEndpoint ? path : `${apiUrl}${path}`;
    const result = await call(
      url,
      params.body,
      params.headers,
      { method: 'POST' },
      sendCorrelationId,
      sendCredentials,
      externalEndpoint,
    );

    if (!result.status) {
      throw Error('No response received');
    }

    if ([204].includes(result.status)) {
      return { response: {}, status: result.status };
    }

    const response = await result.json();

    if ([200, 201, 202, 203].includes(result.status)) {
      return { response, status: result.status };
    }

    if (!response.errorCode || (response.errorCode && !(response.errorCode in KNOWN_ERROR_CODES))) {
      throw Error(`Request failed with response status ${result.status}`);
    }

    return { response, status: result.status };
  } catch (error: unknown) {
    if (process.env.DEBUG) {
      actually.error('Failed Request', error);
    }
    if (error instanceof Error && error.message) {
      throw error;
    }

    throw Error(`Failed to process request, due to an unhandled error.`);
  }
};

export const get = (
  url: string,
  headers?: Headers,
  sendCorrelationId = true,
  sendCredentials = true,
): Promise<Response> => call(url, undefined, headers, { method: 'GET' }, sendCorrelationId, sendCredentials);
